import { createApi as createRtkApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Effect } from 'effect';
import { getClerkTokenEffect, clerkTokenBridgeErrorCause } from './clerkTokenBridge';
import {
  attemptApi,
  attemptApiPromise,
  failApi,
  runApiPromise,
  type ApiRuntimeError,
} from './effect-runtime';
import { withApiResponseEffect, readApiResponseBytesEffect } from './owned-response';
import { API_ORIGIN } from '@/lib/host';
import { OPENROUTER_MODEL_ID } from '@/features/projects/create-constants';
import {
  buildAgentPrompt,
  readyCreateSessionFiles,
  readyRenderAssetPayload,
  readySessionDocuments,
} from '@/features/projects/create-asset-utils';
import type {
  CreateAsset,
  CreateEnvironmentFileInput,
  CreateProject,
  CreateProjectAssetListResponse,
  CreateProjectAssetResponse,
  CreateProjectFileEntry,
  CreateProjectFileResponse,
  CreateProjectFileTreeResponse,
  CreateProjectListResponse,
  CreateProjectSessionResponse,
  CreatePublishResponse,
  CreateRenderArtifact,
  CreateSessionFile,
} from '@/features/projects/create-types';
import type { SessionAgentEvent } from '@/features/_shared/session/session-agent-stream';

type CreateProjectFileArgs = {
  projectId: string;
  path?: string;
  recursive?: boolean;
};

type CreateAgentTurnParams = {
  projectId?: string;
  sessionId: string;
  prompt: string;
  code: string;
  assets: CreateAsset[];
};

type PublishCreateSessionParams = {
  projectId?: string;
  sessionId: string;
  code?: string;
  assets: CreateAsset[];
  artifact?: CreateRenderArtifact | null;
  includeGeneratedImages: boolean;
};

const createBaseQuery = fetchBaseQuery({ baseUrl: API_ORIGIN, credentials: 'omit' });

export const createApi = createRtkApi({
  reducerPath: 'createApi',
  baseQuery: (args, api, extraOptions) =>
    Effect.runPromise(
      Effect.gen(function* () {
        const request = typeof args === 'string' ? { url: args } : args;
        const headers = yield* authHeadersEffect(request.headers);
        yield* attemptApi('token', 'createApi.contentType', () => {
          headers.set('Content-Type', 'application/json');
        });
        return yield* attemptApiPromise('fetch', 'createApi.baseQuery', () =>
          Promise.resolve(createBaseQuery({ ...request, headers }, api, extraOptions)),
        );
      }),
      { signal: api.signal },
    ).catch((error: unknown) => {
      if (api.signal.aborted)
        return { error: { status: 'FETCH_ERROR' as const, error: 'Request aborted' } };
      throw error;
    }),
  tagTypes: ['CreateProjects', 'CreateProjectAssets', 'CreateProjectFiles', 'CreateSessionEvents'],
  endpoints: (build) => ({
    listCreateProjects: build.query<CreateProject[], void>({
      query: () => '/v1/spaces?limit=50&status=active',
      transformResponse: (body: CreateProjectListResponse) =>
        Array.isArray(body.data) ? body.data : [],
      providesTags: ['CreateProjects'],
    }),
    getCreateProject: build.query<CreateProject, string>({
      query: (projectId) => createProjectPath(projectId),
      providesTags: (_r, _e, projectId) => [{ type: 'CreateProjects', id: projectId }],
    }),
    getCreateProjectAssets: build.query<CreateProjectAssetResponse[], string>({
      query: (projectId) => `${createProjectPath(projectId)}/assets?include_content=1`,
      transformResponse: (body: CreateProjectAssetListResponse) =>
        Array.isArray(body.data) ? body.data : [],
      providesTags: (_r, _e, projectId) => [{ type: 'CreateProjectAssets', id: projectId }],
    }),
    getCreateProjectFileTree: build.query<CreateProjectFileEntry[], CreateProjectFileArgs>({
      query: ({ projectId, path = '/', recursive = true }) =>
        createProjectFilesPath(projectId, path, recursive),
      transformResponse: (body: CreateProjectFileTreeResponse) =>
        Array.isArray(body.data) ? body.data : [],
      providesTags: (_r, _e, { projectId }) => [{ type: 'CreateProjectFiles', id: projectId }],
    }),
    getCreateProjectFile: build.query<
      CreateProjectFileResponse,
      { projectId: string; path: string }
    >({
      query: ({ projectId, path }) => createProjectFilesPath(projectId, path, false),
      providesTags: (_r, _e, { projectId, path }) => [
        { type: 'CreateProjectFiles', id: `${projectId}:${path}` },
      ],
    }),
    validateCreateSession: build.query<boolean, string>({
      queryFn(sessionId, api, _extraOptions, fetchWithBQ) {
        return Effect.runPromise(
          attemptApiPromise('fetch', 'createApi.validateSession', () =>
            Promise.resolve(fetchWithBQ(createSessionEventsPath(sessionId))),
          ).pipe(Effect.map((result) => ({ data: !result.error }))),
          { signal: api.signal },
        ).catch((error: unknown) => {
          if (api.signal.aborted)
            return { error: { status: 'FETCH_ERROR' as const, error: 'Request aborted' } };
          throw error;
        });
      },
    }),
    createCreateProject: build.mutation<CreateProject, { title?: string } | void>({
      query: (body) => ({
        url: '/v1/spaces',
        method: 'POST',
        body: {
          title: body?.title ?? 'Untitled space',
          model: OPENROUTER_MODEL_ID,
        },
      }),
      invalidatesTags: ['CreateProjects'],
    }),
    createProjectSession: build.mutation<string, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `${createProjectPath(projectId)}/sessions`,
        method: 'POST',
        body: {
          title: 'Create',
          model: OPENROUTER_MODEL_ID,
        },
      }),
      transformResponse: (body: CreateProjectSessionResponse) => {
        if (!body.id) throw new Error('Space session response missing id');
        return body.id;
      },
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'CreateProjects', id: projectId }],
    }),
    attachCreateProjectAssets: build.mutation<
      void,
      { projectId: string; assets: CreateEnvironmentFileInput[] }
    >({
      query: ({ projectId, assets }) => ({
        url: `${createProjectPath(projectId)}/assets`,
        method: 'POST',
        body: { assets },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'CreateProjectAssets', id: projectId }],
    }),
    putCreateProjectFile: build.mutation<void, { projectId: string; file: CreateSessionFile }>({
      query: ({ projectId, file }) => ({
        url: `${createProjectPath(projectId)}/files`,
        method: 'PUT',
        body: {
          path: file.path,
          content: file.content,
          encoding: file.encoding,
          mime: file.mime,
        },
      }),
      invalidatesTags: (_r, _e, { projectId }) => [{ type: 'CreateProjectFiles', id: projectId }],
    }),
    getCreateSessionEvents: build.query<SessionAgentEvent[], string>({
      query: (sessionId) => createSessionEventsPath(sessionId),
      transformResponse: (body: { data?: SessionAgentEvent[] }) =>
        Array.isArray(body.data) ? body.data : [],
      providesTags: (_r, _e, sessionId) => [{ type: 'CreateSessionEvents', id: sessionId }],
    }),
  }),
});

export const { useCreateCreateProjectMutation, useListCreateProjectsQuery } = createApi;

const authHeadersEffect = Effect.fn('web.createApi.authHeaders')(function* (extra?: HeadersInit) {
  const headers = yield* attemptApi('token', 'createApi.headers', () => new Headers(extra));
  const token = yield* getClerkTokenEffect().pipe(
    Effect.catch((error) =>
      failApi('token', 'createApi.token', clerkTokenBridgeErrorCause(error) ?? error),
    ),
  );
  yield* attemptApi('token', 'createApi.authorization', () => {
    if (token) headers.set('Authorization', `Bearer ${token}`);
  });
  return headers;
});

const readJsonOrThrowEffect = Effect.fn('web.createApi.readJson')(function* <T>(
  response: Response,
): Effect.fn.Return<T, ApiRuntimeError> {
  const bytes = yield* readApiResponseBytesEffect(response);
  return yield* attemptApi('parse', 'createApi.response', () => {
    const text = new TextDecoder().decode(bytes);
    const parsed = text ? (JSON.parse(text) as unknown) : {};
    if (!response.ok) {
      const rawError = (parsed as { error?: unknown }).error;
      const message =
        typeof rawError === 'string'
          ? rawError
          : rawError &&
              typeof rawError === 'object' &&
              typeof (rawError as { message?: unknown }).message === 'string'
            ? (rawError as { message: string }).message
            : `Request failed (${response.status})`;
      throw new Error(message);
    }
    return parsed as T;
  });
});

type ProjectRequest = { url: string; method?: string; body?: unknown };
const projectRequestEffect = Effect.fn('web.createApi.request')(function* <A>(
  makeRequest: () => ProjectRequest,
  consume: (response: Response) => Effect.Effect<A, ApiRuntimeError>,
) {
  const request = yield* attemptApi('parse', 'createApi.request', makeRequest);
  const headers = yield* authHeadersEffect(
    request.method
      ? { 'Content-Type': 'application/json', Accept: 'application/json' }
      : { Accept: 'application/json' },
  );
  const init = yield* attemptApi(
    'parse',
    'createApi.requestBody',
    (): RequestInit => ({
      headers,
      ...(request.method ? { method: request.method } : {}),
      ...('body' in request ? { body: JSON.stringify(request.body) } : {}),
    }),
  );
  return yield* withApiResponseEffect(request.url, init, consume);
});
const projectJsonEffect = <A>(request: () => ProjectRequest) =>
  projectRequestEffect(request, (response) => readJsonOrThrowEffect<A>(response));

function createSessionEventsPath(sessionId: string): string {
  return Effect.runSync(
    Effect.sync(() => `/v1/create/sessions/${encodeURIComponent(sessionId)}/events`),
  );
}

function createProjectPath(projectId: string): string {
  return Effect.runSync(Effect.sync(() => `/v1/spaces/${encodeURIComponent(projectId)}`));
}

function createProjectEventsPath(projectId: string): string {
  return `${createProjectPath(projectId)}/events`;
}

function createProjectFilesPath(projectId: string, path = '/', recursive = true): string {
  const search = new URLSearchParams();
  search.set('path', path);
  if (!recursive) search.set('recursive', '0');
  return `${createProjectPath(projectId)}/files?${search.toString()}`;
}

function createSessionEventsUrl(sessionId: string): string {
  return `${API_ORIGIN}${createSessionEventsPath(sessionId)}`;
}

function createProjectUrl(projectId: string): string {
  return `${API_ORIGIN}${createProjectPath(projectId)}`;
}

function createProjectEventsUrl(projectId: string): string {
  return `${API_ORIGIN}${createProjectEventsPath(projectId)}`;
}

function createProjectFilesUrl(projectId: string, path = '/', recursive = true): string {
  return `${API_ORIGIN}${createProjectFilesPath(projectId, path, recursive)}`;
}

const validateCreateSessionEffect = Effect.fn('web.createApi.validateSession')(function* (
  sessionId: string,
) {
  return yield* projectRequestEffect(
    () => ({ url: createSessionEventsUrl(sessionId) }),
    (response) => Effect.succeed(response.ok),
  );
});

export const listCreateProjectsEffect = Effect.fn('web.createApi.listProjects')(function* () {
  const body = yield* projectJsonEffect<CreateProjectListResponse>(() => ({
    url: `${API_ORIGIN}/v1/spaces?limit=50&status=active`,
  }));
  return yield* attemptApi('parse', 'createApi.listProjection', () =>
    Array.isArray(body.data) ? body.data : [],
  );
});
export function listCreateProjects(): Promise<CreateProject[]> {
  return runApiPromise(listCreateProjectsEffect());
}

export const fetchCreateProjectEffect = Effect.fn('web.createApi.getProject')(function* (
  projectId: string,
) {
  return yield* projectJsonEffect<CreateProject>(() => ({ url: createProjectUrl(projectId) }));
});
export function fetchCreateProject(projectId: string): Promise<CreateProject> {
  return runApiPromise(fetchCreateProjectEffect(projectId));
}

export const fetchCreateProjectAssetsEffect = Effect.fn('web.createApi.getAssets')(function* (
  projectId: string,
) {
  const body = yield* projectJsonEffect<CreateProjectAssetListResponse>(() => ({
    url: `${createProjectUrl(projectId)}/assets?include_content=1`,
  }));
  return yield* attemptApi('parse', 'createApi.listProjection', () =>
    Array.isArray(body.data) ? body.data : [],
  );
});
export function fetchCreateProjectAssets(projectId: string): Promise<CreateProjectAssetResponse[]> {
  return runApiPromise(fetchCreateProjectAssetsEffect(projectId));
}

export const fetchCreateProjectFileTreeEffect = Effect.fn('web.createApi.getFileTree')(function* (
  projectId: string,
) {
  const body = yield* projectJsonEffect<CreateProjectFileTreeResponse>(() => ({
    url: createProjectFilesUrl(projectId, '/', true),
  }));
  return yield* attemptApi('parse', 'createApi.listProjection', () =>
    Array.isArray(body.data) ? body.data : [],
  );
});
export function fetchCreateProjectFileTree(projectId: string): Promise<CreateProjectFileEntry[]> {
  return runApiPromise(fetchCreateProjectFileTreeEffect(projectId));
}

export const fetchCreateProjectFileEffect = Effect.fn('web.createApi.getFile')(function* (
  projectId: string,
  path: string,
) {
  return yield* projectJsonEffect<CreateProjectFileResponse>(() => ({
    url: createProjectFilesUrl(projectId, path, false),
  }));
});
export function fetchCreateProjectFile(
  projectId: string,
  path: string,
): Promise<CreateProjectFileResponse> {
  return runApiPromise(fetchCreateProjectFileEffect(projectId, path));
}

export const putCreateProjectFileEffect = Effect.fn('web.createApi.putFile')(function* (
  projectId: string,
  file: CreateSessionFile,
) {
  yield* projectJsonEffect<CreateProjectFileResponse>(() => ({
    url: `${createProjectUrl(projectId)}/files`,
    method: 'PUT',
    body: { path: file.path, content: file.content, encoding: file.encoding, mime: file.mime },
  }));
});
export function putCreateProjectFile(projectId: string, file: CreateSessionFile): Promise<void> {
  return runApiPromise(putCreateProjectFileEffect(projectId, file));
}

export const createCreateProjectEffect = Effect.fn('web.createApi.createProject')(function* (
  title = 'Untitled space',
) {
  return yield* projectJsonEffect<CreateProject>(() => ({
    url: `${API_ORIGIN}/v1/spaces`,
    method: 'POST',
    body: { title, model: OPENROUTER_MODEL_ID },
  }));
});
export function createCreateProject(title = 'Untitled space'): Promise<CreateProject> {
  return runApiPromise(createCreateProjectEffect(title));
}

const createProjectSessionEffect = Effect.fn('web.createApi.createSession')(function* (
  projectId: string,
) {
  const body = yield* projectJsonEffect<CreateProjectSessionResponse>(() => ({
    url: `${createProjectUrl(projectId)}/sessions`,
    method: 'POST',
    body: { title: 'Create', model: OPENROUTER_MODEL_ID },
  }));
  return yield* attemptApi('parse', 'createApi.sessionId', () => {
    if (!body.id) throw new Error('Space session response missing id');
    return body.id;
  });
});

export const getOrCreateProjectSessionEffect = Effect.fn('web.createApi.getOrCreateSession')(
  function* (projectId: string) {
    const project = yield* fetchCreateProjectEffect(projectId);
    const activeSessionId = yield* attemptApi(
      'parse',
      'createApi.activeSession',
      () => project.active_session_id,
    );
    if (activeSessionId && (yield* validateCreateSessionEffect(activeSessionId)))
      return { project, sessionId: activeSessionId };
    const sessionId = yield* createProjectSessionEffect(projectId);
    const refreshed = yield* fetchCreateProjectEffect(projectId).pipe(
      Effect.catch(() => Effect.succeed({ ...project, active_session_id: sessionId })),
    );
    return { project: refreshed, sessionId };
  },
);
export function getOrCreateProjectSession(
  projectId: string,
): Promise<{ project: CreateProject; sessionId: string }> {
  return runApiPromise(getOrCreateProjectSessionEffect(projectId));
}

export const attachCreateProjectAssetsEffect = Effect.fn('web.createApi.attachAssets')(function* (
  projectId: string,
  assets: CreateEnvironmentFileInput[],
) {
  if (assets.length === 0) return;
  yield* projectJsonEffect<{ ok?: boolean }>(() => ({
    url: `${createProjectUrl(projectId)}/assets`,
    method: 'POST',
    body: { assets },
  }));
});
export function attachCreateProjectAssets(
  projectId: string,
  assets: CreateEnvironmentFileInput[],
): Promise<void> {
  return runApiPromise(attachCreateProjectAssetsEffect(projectId, assets));
}

export const fetchSessionEventsEffect = Effect.fn('web.createApi.getEvents')(function* (
  sessionId: string,
) {
  const body = yield* projectJsonEffect<{ data?: SessionAgentEvent[] }>(() => ({
    url: createSessionEventsUrl(sessionId),
  }));
  return yield* attemptApi('parse', 'createApi.listProjection', () =>
    Array.isArray(body.data) ? body.data : [],
  );
});
export function fetchSessionEvents(sessionId: string): Promise<SessionAgentEvent[]> {
  return runApiPromise(fetchSessionEventsEffect(sessionId));
}

export const postCreateAgentTurnEffect = Effect.fn('web.createApi.postTurn')(function* (
  params: CreateAgentTurnParams,
) {
  yield* projectJsonEffect<{ ok?: boolean }>(() => ({
    url: params.projectId
      ? createProjectEventsUrl(params.projectId)
      : createSessionEventsUrl(params.sessionId),
    method: 'POST',
    body: {
      documents: readySessionDocuments(params.assets),
      files: readyCreateSessionFiles(params.assets),
      events: [
        {
          type: 'user.message',
          content: [
            {
              type: 'text',
              text: buildAgentPrompt({
                prompt: params.prompt,
                code: params.code,
                assets: params.assets,
              }),
            },
          ],
        },
      ],
    },
  }));
});
export function postCreateAgentTurn(params: CreateAgentTurnParams): Promise<void> {
  return runApiPromise(postCreateAgentTurnEffect(params));
}

export const publishCreateSessionEffect = Effect.fn('web.createApi.publish')(function* (
  params: PublishCreateSessionParams,
) {
  return yield* projectJsonEffect<CreatePublishResponse>(() => ({
    url: params.projectId
      ? `${createProjectUrl(params.projectId)}/publish`
      : `${API_ORIGIN}/v1/create/sessions/${encodeURIComponent(params.sessionId)}/publish`,
    method: 'POST',
    body: {
      ...(params.artifact
        ? {
            ...(params.artifact.documentId ? { documentId: params.artifact.documentId } : {}),
            ...(params.artifact.renderId ? { renderId: params.artifact.renderId } : {}),
          }
        : { code: params.code }),
      input: { assets: readyRenderAssetPayload(params.assets) },
      fileName: params.artifact?.fileName ?? 'okra-create-output.pdf',
      timeoutMs: 120_000,
      publish: {
        enabled: true,
        source: {
          enabled: true,
          includeInput: true,
          includeModules: true,
          includeFiles: params.includeGeneratedImages,
        },
      },
      metadata: { source: 'create-page-publish-button' },
    },
  }));
});
export function publishCreateSession(
  params: PublishCreateSessionParams,
): Promise<CreatePublishResponse> {
  return runApiPromise(publishCreateSessionEffect(params));
}

const responseBlobEffect = Effect.fn('web.createApi.previewBlob')(function* (response: Response) {
  const bytes = yield* readApiResponseBytesEffect(response);
  return yield* attemptApi(
    'parse',
    'createApi.previewBlob',
    () =>
      new Blob([bytes as Uint8Array<ArrayBuffer>], {
        type: response.headers.get('content-type') ?? '',
      }),
  );
});
export const fetchDocumentPdfEffect = Effect.fn('web.createApi.previewPdf')(function* (
  documentId: string,
) {
  const headers = yield* authHeadersEffect({ Accept: 'application/pdf' });
  const path = yield* attemptApi('parse', 'createApi.previewPath', () =>
    encodeURIComponent(documentId),
  );
  const primary = yield* withApiResponseEffect(
    `${API_ORIGIN}/document/${path}/download`,
    { headers },
    (response) => (response.ok ? responseBlobEffect(response) : Effect.succeed(null)),
  );
  if (primary) return primary;
  return yield* withApiResponseEffect(
    `${API_ORIGIN}/v1/documents/${path}/original.pdf`,
    { headers },
    (response) =>
      response.ok
        ? responseBlobEffect(response)
        : failApi(
            'fetch',
            'createApi.previewStatus',
            new Error(`Preview fetch failed (${response.status})`),
          ),
  );
});
export function fetchDocumentPdf(documentId: string): Promise<Blob> {
  return runApiPromise(fetchDocumentPdfEffect(documentId));
}
